import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "./AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Lock, Mail, Shield, ArrowLeft, KeyRound, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Steps: "login" | "otp" | "forgot" | "reset"
  const resetToken = searchParams.get("token");
  const [step, setStep] = useState(resetToken ? "reset" : "login");

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(true);

  // OTP state
  const [otp, setOtp] = useState("");
  const [deviceRemember, setDeviceRemember] = useState(true);

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // Reset password state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email, password, stay_logged_in: stayLoggedIn
      }, { withCredentials: true });
      const data = res.data;
      if (data.requires_otp || data.requires_2fa) {
        toast.success("Verification code sent to your email!");
        setStep("otp");
      } else {
        await checkAuth();
        navigate("/staff");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        setError("Invalid email or password. Please try again.");
      } else if (status === 429) {
        setError("Too many failed attempts. Please wait 15 minutes.");
      } else if (status === 422) {
        setError("Please enter both email and password.");
      } else if (detail) {
        setError(formatApiError(detail));
      } else {
        console.error("Login error:", err.message, err.code);
        setError("Unable to connect to server. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify OTP ── */
  const handleOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        email, otp, device_remember: deviceRemember, stay_logged_in: stayLoggedIn
      }, { withCredentials: true });
      await checkAuth();
      navigate("/staff");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        setError("Invalid or expired code. Check your email for the latest code.");
      } else if (detail) {
        setError(formatApiError(detail));
      } else {
        setError("Unable to verify. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot Password ── */
  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  /* ── Reset Password ── */
  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success("Password updated! You can now log in.");
      setStep("login");
      navigate("/staff/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4" data-testid="login-page">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            {step === "reset" ? <KeyRound className="w-6 h-6 text-white" /> : step === "forgot" ? <Mail className="w-6 h-6 text-white" /> : step === "otp" ? <Shield className="w-6 h-6 text-white" /> : <Lock className="w-6 h-6 text-white" />}
          </div>
          <CardTitle className="text-xl font-bold text-gray-900">
            {step === "reset" ? "Set New Password" : step === "forgot" ? "Reset Password" : step === "otp" ? "Two-Factor Auth" : "Staff Login"}
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            {step === "reset" ? "Enter your new password below" : step === "forgot" ? "Enter your email to receive a reset link" : step === "otp" ? "Enter the 6-digit code sent to your email" : "Fit Print Bros Staff Portal"}
          </p>
        </CardHeader>
        <CardContent className="space-y-4 pt-2">
          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">{error}</div>}

          {/* ── LOGIN FORM ── */}
          {step === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    className="pl-10" placeholder="you@fitprintbros.com" data-testid="login-email" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                    className="pl-10 pr-10" placeholder="Enter password" data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    data-testid="toggle-password" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={stayLoggedIn} onChange={e => setStayLoggedIn(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500" data-testid="stay-logged-in" />
                  <span className="text-sm text-gray-600">Stay logged in for 7 days</span>
                </label>
                <button type="button" onClick={() => { setStep("forgot"); setForgotEmail(email); setError(""); }}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>

              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                disabled={loading} data-testid="login-submit">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          )}

          {/* ── OTP FORM ── */}
          {step === "otp" && (
            <form onSubmit={handleOTP} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Verification Code</Label>
                <Input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                  maxLength={6} className="text-center text-2xl tracking-[0.5em] font-mono" placeholder="000000"
                  autoFocus data-testid="otp-input" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={deviceRemember} onChange={e => setDeviceRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500" data-testid="remember-device" />
                <span className="text-sm text-gray-600">Remember this device for 30 days</span>
              </label>
              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white"
                disabled={loading || otp.length !== 6} data-testid="otp-submit">
                {loading ? "Verifying..." : "Verify & Sign In"}
              </Button>
              <button type="button" onClick={() => { setStep("login"); setOtp(""); setError(""); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to login
              </button>
            </form>
          )}

          {/* ── FORGOT PASSWORD FORM ── */}
          {step === "forgot" && (
            <form onSubmit={handleForgot} className="space-y-4">
              {forgotSent ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-green-600" />
                  </div>
                  <p className="text-sm text-gray-700 font-medium">Reset link sent!</p>
                  <p className="text-sm text-gray-500 mt-1">Check your email for a link to reset your password. It expires in 1 hour.</p>
                  <Button type="button" variant="outline" className="mt-4"
                    onClick={() => { setStep("login"); setForgotSent(false); setError(""); }} data-testid="back-to-login">
                    <ArrowLeft className="w-3 h-3 mr-1" /> Back to Login
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-sm font-medium">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                        className="pl-10" placeholder="you@fitprintbros.com" data-testid="forgot-email" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white"
                    disabled={loading} data-testid="forgot-submit">
                    {loading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <button type="button" onClick={() => { setStep("login"); setError(""); }}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to login
                  </button>
                </>
              )}
            </form>
          )}

          {/* ── RESET PASSWORD FORM ── */}
          {step === "reset" && (
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                    className="pl-10 pr-10" placeholder="Min 6 characters" data-testid="new-password" />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-new-password">
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                    className="pl-10" placeholder="Re-enter password" data-testid="confirm-password" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white"
                disabled={loading} data-testid="reset-submit">
                {loading ? "Updating..." : "Set New Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <button
        onClick={() => window.open("/", "_blank")}
        className="mt-4 text-sm text-zinc-400 hover:text-white flex items-center gap-1.5 transition"
        data-testid="login-back-to-site"
      >
        <ExternalLink className="w-3.5 h-3.5" /> Open Website
      </button>
    </div>
  );
}
